import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Badge,
  IconButton,
  Tooltip,
  useColorModeValue,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Switch,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon, EditIcon } from '@chakra-ui/icons';
import Card from 'components/card/Card';
import { getData, postData, putData, deleteData } from '../../../utils/httpClient';
import { useSearch } from '../../../contexts/SearchContext';

export default function StudyTypesView() {
  const toast = useToast();
  const { searchQuery } = useSearch();

  const [loading, setLoading] = useState(false);
  const [studyTypes, setStudyTypes] = useState([]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // create | edit
  const [selected, setSelected] = useState(null);
  const [formData, setFormData] = useState({
    description: '',
    enabled: true,
  });

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  const fetchStudyTypes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getData('/studyTypes');
      setStudyTypes(res?.payload?.studyTypes || []);
    } catch (e) {
      setStudyTypes([]);
      toast({
        title: 'Error al cargar tipos de estudio',
        description: e?.message,
        status: 'error',
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchStudyTypes();
  }, [fetchStudyTypes]);

  const filtered = useMemo(() => {
    const q = (searchQuery || '').toLowerCase().trim();
    const list = Array.isArray(studyTypes) ? studyTypes : [];
    if (!q) return list;
    return list.filter((t) => (t.description || '').toLowerCase().includes(q));
  }, [studyTypes, searchQuery]);

  const openCreate = () => {
    setModalMode('create');
    setSelected(null);
    setFormData({ description: '', enabled: true });
    setIsModalOpen(true);
  };

  const openEdit = (row) => {
    setModalMode('edit');
    setSelected(row);
    setFormData({
      description: row?.description || '',
      enabled: row?.enabled !== undefined ? !!row.enabled : true,
    });
    setIsModalOpen(true);
  };

  const submit = async () => {
    const desc = (formData.description || '').trim();
    if (!desc) {
      toast({ title: 'Ingrese una descripción', status: 'warning', duration: 2500 });
      return;
    }
    try {
      if (modalMode === 'create') {
        await postData('/studyTypes', { description: desc });
        toast({ title: 'Tipo de estudio creado', status: 'success', duration: 2500 });
      } else {
        await putData(`/studyTypes/${selected?.id}`, {
          description: desc,
          enabled: !!formData.enabled,
        });
        toast({ title: 'Tipo de estudio actualizado', status: 'success', duration: 2500 });
      }
      setIsModalOpen(false);
      fetchStudyTypes();
    } catch (e) {
      toast({
        title: 'No se pudo guardar',
        description: e?.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const remove = async (row) => {
    if (!window.confirm('¿Dar de baja este tipo de estudio?')) return;
    try {
      await deleteData(`/studyTypes/${row.id}`);
      toast({ title: 'Tipo de estudio dado de baja', status: 'success', duration: 2500 });
      fetchStudyTypes();
    } catch (e) {
      toast({
        title: 'No se pudo dar de baja',
        description: e?.message,
        status: 'error',
        duration: 3500,
        isClosable: true,
      });
    }
  };

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Card direction="column" w="100%" px="0px" overflowX={{ sm: 'scroll', lg: 'hidden' }}>
        <Flex px="25px" justify="space-between" mb="20px" align="center">
          <Box>
            <Text color={textColor} fontSize="22px" fontWeight="700" lineHeight="100%">
              Tipos de estudio
            </Text>
            <Text mt={1} color="gray.500" fontSize="sm">
              Gestión de tipos para crear nuevos estudios.
            </Text>
          </Box>
          <Button leftIcon={<AddIcon />} colorScheme="brand" variant="solid" onClick={openCreate}>
            Nuevo tipo
          </Button>
        </Flex>

        <Table variant="simple" color="gray.500" mb="24px">
          <Thead>
            <Tr>
              <Th borderColor={borderColor}>Descripción</Th>
              <Th borderColor={borderColor}>Estado</Th>
              <Th borderColor={borderColor}>Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filtered.map((row) => (
              <Tr key={row.id} _hover={{ bg: hoverBg }}>
                <Td borderColor={borderColor}>
                  <Text color={textColor} fontSize="sm" fontWeight="700">
                    {row.description}
                  </Text>
                </Td>
                <Td borderColor={borderColor}>
                  <Badge colorScheme={row.enabled ? 'green' : 'red'}>
                    {row.enabled ? 'Activo' : 'Inactivo'}
                  </Badge>
                </Td>
                <Td borderColor={borderColor}>
                  <Flex gap="2">
                    <Tooltip label="Editar">
                      <IconButton
                        icon={<EditIcon />}
                        size="sm"
                        colorScheme="blue"
                        variant="ghost"
                        onClick={() => openEdit(row)}
                      />
                    </Tooltip>
                    <Tooltip label="Dar de baja">
                      <IconButton
                        icon={<DeleteIcon />}
                        size="sm"
                        colorScheme="red"
                        variant="ghost"
                        onClick={() => remove(row)}
                      />
                    </Tooltip>
                  </Flex>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>

        {!loading && filtered.length === 0 && (
          <Text textAlign="center" py="20px" color="gray.500">
            No se encontraron tipos de estudio
          </Text>
        )}
      </Card>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{modalMode === 'create' ? 'Nuevo tipo de estudio' : 'Editar tipo de estudio'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isRequired>
              <FormLabel>Descripción</FormLabel>
              <Input
                value={formData.description}
                onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
                placeholder="Ej: Carrera / Curso / Taller…"
              />
            </FormControl>
            {modalMode === 'edit' && (
              <FormControl display="flex" alignItems="center" mt={4}>
                <FormLabel mb="0">Activo</FormLabel>
                <Switch
                  isChecked={!!formData.enabled}
                  onChange={(e) => setFormData((p) => ({ ...p, enabled: e.target.checked }))}
                />
              </FormControl>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setIsModalOpen(false)}>
              Cancelar
            </Button>
            <Button colorScheme="blue" onClick={submit}>
              Guardar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

